import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { CornerTicks, Reveal, SectionHeading } from "./decor";
import { EmailReveal } from "./EmailReveal";
import { ASSETS } from "../data/siteData";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  promoter_name: "",
  email: "",
  phone: "",
  event_date: "",
  venue: "",
  city: "",
  country: "",
  event_type: "Club Show",
  audience_size: "",
  budget_range: "",
  message: "",
};

const inputCls =
  "w-full bg-[#0D0D10] border border-zinc-800 px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:ring-2 focus:ring-red-600 focus:outline-none transition-shadow [color-scheme:dark]";

const Field = ({ label, testId, children }) => (
  <label className="block">
    <span className="font-mono-tech text-[11px] tracking-[0.25em] uppercase text-zinc-400 block mb-2">{label}</span>
    <span data-testid={testId} className="block">{children}</span>
  </label>
);

export const BookingForm = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const { t } = useLang();
  const L = t.booking.labels;
  const P = t.booking.placeholders;

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success(t.booking.toastOk);
      setForm(initialForm);
    } catch (err) {
      toast.error(t.booking.toastErr);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="booking" data-testid="booking-section" className="relative py-24 sm:py-32 bg-[#0A0A0C]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeading kicker={t.booking.kicker} title={t.booking.title} testId="booking-heading" />

        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-16 items-start">
          <div>
            <Reveal className="relative inline-block mb-8">
              <img src={ASSETS.logo} alt="CRAYFOX logo" className="w-32 h-32 object-cover border border-zinc-800" />
              <CornerTicks className="border-red-600/60" />
            </Reveal>
            <Reveal delay={0.05}>
              <p className="text-base sm:text-lg text-zinc-300 leading-relaxed">{t.booking.pitch}</p>
            </Reveal>
            <Reveal delay={0.1} className="mt-8 space-y-4">
              {t.booking.bullets.map((point) => (
                <p key={point} className="flex items-start gap-3 text-sm sm:text-base text-zinc-300">
                  <CheckCircle2 size={18} className="text-red-600 shrink-0 mt-0.5" />
                  {point}
                </p>
              ))}
            </Reveal>
            <Reveal delay={0.15} className="mt-8 border-t border-zinc-800 pt-6">
              <p className="font-mono-tech text-xs tracking-[0.25em] uppercase text-zinc-500">{t.booking.direct}</p>
              <EmailReveal testId="booking-email-me" />
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <form
              data-testid="booking-form"
              onSubmit={submit}
              className="relative bg-[#121216]/80 backdrop-blur-xl border border-zinc-800/80 shadow-[0_8px_32px_rgba(0,0,0,0.8)] p-6 sm:p-10"
            >
              <CornerTicks className="border-red-600/50" />
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label={L.name} testId="booking-name-field">
                  <input data-testid="booking-name-input" required value={form.promoter_name} onChange={set("promoter_name")} placeholder={P.name} className={inputCls} />
                </Field>
                <Field label={L.email} testId="booking-email-field">
                  <input data-testid="booking-email-input" required type="email" value={form.email} onChange={set("email")} placeholder={P.email} className={inputCls} />
                </Field>
                <Field label={L.phone} testId="booking-phone-field">
                  <input data-testid="booking-phone-input" value={form.phone} onChange={set("phone")} placeholder={P.phone} className={inputCls} />
                </Field>
                <Field label={L.date} testId="booking-date-field">
                  <input data-testid="booking-date-input" required type="date" value={form.event_date} onChange={set("event_date")} className={inputCls} />
                </Field>
                <Field label={L.venue} testId="booking-venue-field">
                  <input data-testid="booking-venue-input" required value={form.venue} onChange={set("venue")} placeholder={P.venue} className={inputCls} />
                </Field>
                <Field label={L.city} testId="booking-city-field">
                  <input data-testid="booking-city-input" required value={form.city} onChange={set("city")} placeholder={P.city} className={inputCls} />
                </Field>
                <Field label={L.country} testId="booking-country-field">
                  <input data-testid="booking-country-input" value={form.country} onChange={set("country")} placeholder={P.country} className={inputCls} />
                </Field>
                <Field label={L.type} testId="booking-type-field">
                  <select data-testid="booking-type-select" value={form.event_type} onChange={set("event_type")} className={inputCls}>
                    {t.booking.types.map((o) => (
                      <option key={o.v} value={o.v}>{o.l}</option>
                    ))}
                  </select>
                </Field>
                <Field label={L.audience} testId="booking-audience-field">
                  <select data-testid="booking-audience-select" value={form.audience_size} onChange={set("audience_size")} className={inputCls}>
                    <option value="">{t.booking.select}</option>
                    {t.booking.audiences.map((o) => (
                      <option key={o.v} value={o.v}>{o.l}</option>
                    ))}
                  </select>
                </Field>
                <Field label={L.budget} testId="booking-budget-field">
                  <select data-testid="booking-budget-select" value={form.budget_range} onChange={set("budget_range")} className={inputCls}>
                    <option value="">{t.booking.select}</option>
                    {t.booking.budgets.map((o) => (
                      <option key={o.v} value={o.v}>{o.l}</option>
                    ))}
                  </select>
                </Field>
                <div className="sm:col-span-2">
                  <Field label={L.message} testId="booking-message-field">
                    <textarea
                      data-testid="booking-message-input"
                      rows={4}
                      value={form.message}
                      onChange={set("message")}
                      placeholder={P.message}
                      className={inputCls}
                    />
                  </Field>
                </div>
              </div>
              <button
                data-testid="booking-submit-button"
                type="submit"
                disabled={sending}
                className="mt-8 w-full font-mono-tech text-sm tracking-[0.3em] uppercase bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white px-8 py-4 transition-colors duration-300 shadow-[0_0_30px_rgba(220,38,38,0.3)]"
              >
                {sending ? t.booking.sending : t.booking.submit}
              </button>
              <p className="mt-4 font-mono-tech text-[11px] tracking-[0.15em] text-zinc-500 text-center uppercase">
                {t.booking.note}
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
